import { functionAppScope } from "../authConfig";
import { msalInstance } from "../index";
import {
  InteractionRequiredAuthError,
  BrowserAuthError,
  AccountInfo,
} from "@azure/msal-browser";
const apiUrl = process.env.REACT_APP_API_URL ?? "";
const DEFAULT_TIMEOUT_MS = 30000;
const TIMEOUT_ERROR_COOLDOWN = 10000;
const MAX_RETRY_ATTEMPTS = 1;

let lastTimeoutError = 0;
let isRedirecting = false;
const retryAttempts = new Map<string, number>();

export interface HttpRequestConfig {
  params?: Record<string, unknown> | URLSearchParams;
  headers?: Record<string, string | number | boolean | null | undefined>;
  responseType?: "json" | "blob" | "text";
  timeout?: number;
  signal?: AbortSignal;
  data?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

interface HttpErrorResponse<T = unknown> {
  status: number;
  data: T;
  headers: Record<string, string>;
}

export class HttpError<T = unknown> extends Error {
  status: number;
  response: HttpErrorResponse<T>;
  data: T;
  config?: { method: string; url: string };

  constructor(
    message: string,
    status: number,
    data: T,
    headers: Record<string, string>,
    config?: { method: string; url: string }
  ) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.data = data;
    this.response = { status, data, headers };
    this.config = config;
  }
}

export class AuthNotReadyError extends Error {
  constructor() {
    super("Auth not ready — no account or token available");
    this.name = "AuthNotReadyError";
  }
}

export class TimeoutError extends Error {
  config: { method: string; url: string };
  constructor(message: string, config: { method: string; url: string }) {
    super(message);
    this.name = "TimeoutError";
    this.config = config;
  }
}

function getAccount(): AccountInfo | null {
  return (
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0] ??
    null
  );
}

async function fetchAccessToken(forceRefresh = false): Promise<string | null> {
  const account = getAccount();
  if (!account) throw new AuthNotReadyError();

  if (
    !localStorage.getItem("loggedInUserEmail") ||
    !localStorage.getItem("loggedInUserFullName")
  ) {
    if (account.username && account.name) {
      if (!localStorage.getItem("loggedInUserEmail")) {
        localStorage.setItem("loggedInUserEmail", account.username);
      }
      if (!localStorage.getItem("loggedInUserFullName")) {
        localStorage.setItem("loggedInUserFullName", account.name);
      }
    }
  }

  const now = Date.now();
  if (now - lastTimeoutError < TIMEOUT_ERROR_COOLDOWN) {
    throw new Error("Token acquisition skipped due to recent timeout");
  }

  try {
    const resp = await msalInstance.acquireTokenSilent({
      ...functionAppScope,
      account,
      forceRefresh,
    });
    isRedirecting = false;
    return resp.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      if (!isRedirecting) {
        isRedirecting = true;
        await msalInstance.acquireTokenRedirect({
          ...functionAppScope,
          account,
        });
      }
      throw new AuthNotReadyError();
    }
    if (e instanceof BrowserAuthError && e.errorCode === "monitor_window_timeout") {
      lastTimeoutError = Date.now();
    }
    throw e;
  }
}

function headersToObject(h: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  h.forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}

function appendSearchParam(search: URLSearchParams, key: string, value: unknown) {
  if (value === null || value === undefined || value === "") return;
  if (Array.isArray(value)) {
    for (const v of value) {
      if (v !== null && v !== undefined && v !== "") {
        search.append(key, String(v));
      }
    }
    return;
  }
  if (value instanceof Date) {
    search.append(key, value.toISOString());
    return;
  }
  search.append(key, String(value));
}

function buildPathWithParams(path: string, params?: HttpRequestConfig["params"]) {
  if (!params) return path;
  const search = new URLSearchParams();
  if (params instanceof URLSearchParams) {
    params.forEach((v, k) => search.append(k, v));
  } else {
    for (const [k, v] of Object.entries(params)) {
      appendSearchParam(search, k, v);
    }
  }
  const queryString = search.toString();
  if (!queryString) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${queryString}`;
}

function buildFullUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path;
  const base = (apiUrl || "").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (!base) return normalizedPath;
  return `${base}${normalizedPath}`;
}

function extractServerMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const obj = data as Record<string, unknown>;
  if (typeof obj.message === "string" && obj.message.trim()) return obj.message;
  if (typeof obj.title === "string" && obj.title.trim()) return obj.title;
  if (typeof obj.error === "string" && obj.error.trim()) return obj.error;
  if (typeof obj.detail === "string" && obj.detail.trim()) return obj.detail;
  return undefined;
}

async function parseResponseBody(
  response: Response,
  responseType?: HttpRequestConfig["responseType"]
): Promise<unknown> {
  if (responseType === "blob") return response.blob();
  if (responseType === "text") return response.text();

  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json") || contentType.includes("+json")) {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function buildHeaders(
  accessToken: string,
  body: unknown,
  extraHeaders?: HttpRequestConfig["headers"]
): Record<string, string> {
  const headers: Record<string, string> = {};
  const isFormData = body instanceof FormData;
  if (body !== undefined && body !== null && !isFormData && typeof body !== "string") {
    headers["Content-Type"] = "application/json";
  }
  if (extraHeaders) {
    for (const [key, value] of Object.entries(extraHeaders)) {
      if (value === null || value === undefined) continue;
      if (
        isFormData &&
        key.toLowerCase() === "content-type"
      ) {
        continue;
      }
      headers[key] = String(value);
    }
  }

  headers["Authorization"] = `Bearer ${accessToken}`;
  headers["BonusType"] = localStorage.getItem("cba.bonusType") || "ClosingBonus";
  headers["RoleType"] =
    headers["RoleType"] || localStorage.getItem("cba.role") || "";

  return headers;
}

async function handleResponseError(
  response: Response,
  method: string,
  url: string,
  requestFn: () => Promise<HttpResponse>,
  headers: Record<string, string>
): Promise<HttpResponse> {
  const responseData = await parseResponseBody(response);

  if (response.status === 401) {
    const requestKey = `${method}-${url}`;
    const attempts = retryAttempts.get(requestKey) || 0;
    if (attempts < MAX_RETRY_ATTEMPTS) {
      retryAttempts.set(requestKey, attempts + 1);
      try {
        await fetchAccessToken(true);
        retryAttempts.delete(requestKey);
        return requestFn();
      } catch (refreshError) {
        retryAttempts.delete(requestKey);
        if (!isRedirecting) {
          isRedirecting = true;
          msalInstance.loginRedirect();
        }
        throw refreshError;
      }
    }
    retryAttempts.delete(requestKey);
    if (!isRedirecting) {
      isRedirecting = true;
      msalInstance.loginRedirect();
    }
  }

  if (response.status === 403) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0 && !isRedirecting) {
      isRedirecting = true;
      msalInstance.loginRedirect();
    }
  }

  const serverMessage = extractServerMessage(responseData);
  const message =
    response.status === 403
      ? serverMessage || "You do not have permission to perform this action."
      : serverMessage || `Request failed with status ${response.status}`;

  throw new HttpError(
    message,
    response.status,
    responseData,
    headers,
    { method, url }
  );
}

async function request<T = any>(
  method: string,
  path: string,
  data?: unknown,
  config?: HttpRequestConfig
): Promise<HttpResponse<T>> {
  const urlWithParams = method === "GET" ? buildPathWithParams(path, config?.params) : path;
  const url = buildFullUrl(urlWithParams);
  const body = data ?? config?.data;
  const timeoutMs = config?.timeout ?? DEFAULT_TIMEOUT_MS;

  const doRequest = async (): Promise<HttpResponse<T>> => {
    const accessToken = await fetchAccessToken();
    const headers = buildHeaders(accessToken || "", body, config?.headers);

    const controller = new AbortController();
    const externalAbortHandler = () => controller.abort();
    if (config?.signal) {
      if (config.signal.aborted) controller.abort();
      else config.signal.addEventListener("abort", externalAbortHandler);
    }
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const isBodyRaw =
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      body instanceof URLSearchParams ||
      ArrayBuffer.isView(body) ||
      typeof body === "string";

    const fetchOptions: RequestInit = {
      method: method.toUpperCase(),
      headers,
      signal: controller.signal,
      ...(body !== undefined
        ? { body: isBodyRaw ? (body as BodyInit) : JSON.stringify(body) }
        : {}),
    };

    try {
      const response = await fetch(url, fetchOptions);
      const responseHeaders = headersToObject(response.headers);
      if (!response.ok) {
        return handleResponseError(
          response,
          method.toUpperCase(),
          path,
          doRequest as () => Promise<HttpResponse>,
          responseHeaders
        ) as Promise<HttpResponse<T>>;
      }
      const responseData = (await parseResponseBody(
        response,
        config?.responseType
      )) as T;
      return {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
      };
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === "AbortError") {
        throw new TimeoutError(`Request timed out after ${timeoutMs}ms`, {
          method: method.toUpperCase(),
          url: path,
        });
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
      if (config?.signal) {
        config.signal.removeEventListener("abort", externalAbortHandler);
      }
    }
  };

  return doRequest();
}

const httpClient = {
  get: <T = any>(path: string, config?: HttpRequestConfig) =>
    request<T>("GET", path, undefined, config),
  post: <T = any>(path: string, body?: unknown, config?: HttpRequestConfig) =>
    request<T>("POST", path, body, config),
  put: <T = any>(path: string, body?: unknown, config?: HttpRequestConfig) =>
    request<T>("PUT", path, body, config),
  delete: <T = any>(path: string, config?: HttpRequestConfig) =>
    request<T>("DELETE", path, undefined, config),
};

export default httpClient;
